<template>
    <div class="page-head">
        <div v-if="back.to" class="page-head__back">
            <UiButtonBack class="page-head__back-link" @click="backRoute">
                {{ back.text }}
            </UiButtonBack>
            <div v-if="$slots.back_control" class="page-head__back-control">
                <slot name="back_control" />
            </div>
        </div>
        <div class="page-head__content">
            <div class="page-head__content-info">
                <div class="page-head__content-info__top">
                    <UIHeading v-if="$slots.title" level="1" class="page-head__content-info__top-title">
                        <slot name="title" />
                    </UIHeading>
                    <div v-if="$slots.top_control" class="page-head__content-info__top-control">
                        <slot name="top_control" />
                    </div>
                </div>
                <div v-if="$slots.description" class="page-head__content-info__description">
                    <slot name="description" />
                </div>
            </div>
            <div v-if="$slots.components" class="page-head__content-component">
                <slot name="components" />
            </div>
        </div>
        <div v-if="$slots.controls" class="page-head__controls">
            <slot name="controls" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { pageHeadProps } from './type'

    const props = defineProps<pageHeadProps>()
    const router = useRouter()
    const back = computed(() => {
        return {
            to: props.backLink,
            text: props.backText,
        }
    })

    const backRoute = () => {
        router.push(back.value.to)
    }
 </script>

<style lang="scss" scoped>
    .page-head {
        width: 100%;

        &__back {
            margin-bottom: 28px;

            @include respond-to(mobile) {
                position: fixed;
                top: 0;
                left: 0;
                z-index: 500;
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                height: 52px;
                margin-bottom: 0;
                padding: 14px 16px;
                border-radius: 0 0 16px 16px;
                background-color: var(--white);
            }

            &-link {
                color: var(--indigo-400);
                transition: color $default-transition;

                :deep(.icon) {
                    width: 14px;
                    height: 14px;
                    color: var(--indigo-400);
                }

                @include hover {
                    color: var(--indigo-600);

                    :deep(.icon) {
                        color: var(--indigo-600);
                    }
                }

                @include respond-to(mobile) {
                    color: var(--blue-600);

                    @include hover {
                        color: var(--blue-600);

                        :deep(.icon) {
                            color: var(--blue-600);
                        }
                    }

                    @include manrope14r;
                }
            }
        }

        &__content {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            padding: 24px;
            border-radius: 24px;
            background-color: var(--white);
            gap: 0 24px;

            @include respond-to(mobile) {
                flex-flow: column;
                margin-bottom: 8px;
                padding: 16px;
            }

            &-info {
                &__top {
                    display: flex;
                    align-items: center;
                    margin-bottom: 12px;

                    @include respond-to(mobile) {
                        justify-content: space-between;
                        margin-bottom: 8px;
                    }

                    &-title {
                        @include mulish32b;

                        @include respond-to(mobile) {
                            @include mulish20b;
                        }
                    }

                    &-control {
                        margin-left: 8px;
                    }
                }

                &__description {
                    padding-right: 45px;
                }
            }

            &-component {
                @include respond-to(mobile) {
                    margin-top: 16px;
                }
            }
        }

        &__controls {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0 24px;
            width: 100%;
            margin-top: 32px;
            margin-bottom: 20px;

            @include respond-to(mobile) {
                flex-flow: row wrap;
                margin-top: 8px;
                margin-bottom: 8px;
                padding: 16px;
                border-radius: 20px;
                background-color: var(--white);
            }
        }
    }
</style>
